/**
 * Status workflow configuration for Service Requests and Work Schedules
 * 
 * KEY RULES:
 * 1. SR statuses (pre-scheduling): NEW, ESTIMATE, APPROVAL, PLANNING, CANCELLED
 *    - SR_SCHEDULED is set automatically when Work Schedule is created (not user-selectable)
 * 2. WS statuses (post-scheduling): SCHEDULED, IN_PROCESS, ON_HOLD, QA_REVIEW, RELEASE, BILLING, INVOICED, CLOSED, CANCELLED, RESCHEDULE
 * 3. CANCELLED: Available to Operator+ roles, requires confirmation
 * 4. UNCANCEL: Available to Manager+ roles only, restores previous status
 * 5. RESCHEDULE: Available for Work Schedules, keeps same record for rescheduling
 */

// ==========================================
// ROLE PERMISSIONS
// ==========================================

export const ROLE_HIERARCHY = ['TECHNICIAN', 'OPERATOR', 'MANAGER', 'ADMIN'];

export const canCancel = (userRole) => {
  const roleIndex = ROLE_HIERARCHY.indexOf(userRole);
  const operatorIndex = ROLE_HIERARCHY.indexOf('OPERATOR');
  return roleIndex >= operatorIndex;
};

export const canUncancel = (userRole) => {
  const roleIndex = ROLE_HIERARCHY.indexOf(userRole);
  const managerIndex = ROLE_HIERARCHY.indexOf('MANAGER');
  return roleIndex >= managerIndex;
};

// ==========================================
// SERVICE REQUEST STATUS WORKFLOW (PRE-SCHEDULING)
// ==========================================

// User-selectable SR statuses (excludes SR_SCHEDULED which is system-set)
export const SR_USER_STATUSES = ['NEW', 'ESTIMATE', 'APPROVAL', 'PLANNING'];

// All SR statuses including system-set ones
export const SR_STATUSES = ['NEW', 'ESTIMATE', 'APPROVAL', 'PLANNING', 'SR_SCHEDULED', 'CANCELLED'];

export const SR_STATUS_CONFIG = {
  NEW: {
    label: 'New',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    hex: '#3B82F6',
    description: 'Newly created service request',
    userSelectable: true,
    icon: 'Sparkles'  // Star icon for NEW status
  },
  ESTIMATE: {
    label: 'Estimate',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    hex: '#3B82F6',
    description: 'Awaiting estimate from facility',
    userSelectable: true
  },
  APPROVAL: {
    label: 'Approval',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    hex: '#F97316',
    description: 'Awaiting customer approval',
    userSelectable: true
  },
  PLANNING: {
    label: 'Planning',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    hex: '#8B5CF6',
    description: 'Approved, planning work',
    userSelectable: true
  },
  SR_SCHEDULED: {
    label: 'Scheduled',
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    hex: '#14B8A6',
    description: 'Work Schedule created - status changes on Work Schedule',
    userSelectable: false, // System-set only
    isTerminal: true
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-700 border-red-200',
    hex: '#EF4444',
    description: 'Service request cancelled',
    userSelectable: true,
    requiresConfirmation: true,
    minRole: 'OPERATOR'
  }
};

// SR status transitions (what each status can move to)
// Note: SR_SCHEDULED is NOT in any transition list - it's set only by Work Schedule creation
export const SR_STATUS_TRANSITIONS = {
  NEW: ['ESTIMATE', 'APPROVAL', 'PLANNING'], // Removed SCHEDULED - can skip steps but not to SCHEDULED
  ESTIMATE: ['NEW', 'APPROVAL'],
  APPROVAL: ['ESTIMATE', 'PLANNING'],
  PLANNING: ['APPROVAL'], // Cannot go to SR_SCHEDULED manually
  SR_SCHEDULED: [], // Terminal - changes happen on Work Schedule
  CANCELLED: [] // Terminal unless UNCANCEL
};

// Follow-up thresholds (days)
export const SR_FOLLOWUP_THRESHOLDS = {
  ESTIMATE: 2,
  APPROVAL: 3
};

// Auto-archive threshold for CANCELLED status (days)
export const CANCELLED_AUTO_ARCHIVE_DAYS = 60;

// ==========================================
// WORK SCHEDULE STATUS WORKFLOW (POST-SCHEDULING)
// ==========================================

// Standard WS statuses in linear order
export const WS_STATUS_ORDER = ['SCHEDULED', 'IN_PROCESS', 'ON_HOLD', 'COMPLETE', 'INCOMPLETE', 'QA_REVIEW', 'RELEASE', 'BILLING', 'INVOICED', 'CLOSED'];

// All WS statuses including special ones
export const WS_STATUSES = [...WS_STATUS_ORDER, 'CANCELLED', 'RESCHEDULE'];

export const WS_STATUS_CONFIG = {
  SCHEDULED: {
    label: 'Scheduled',
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    hex: '#14B8A6',
    description: 'Work is scheduled',
    icon: 'Calendar'
  },
  IN_PROCESS: {
    label: 'In Process',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    hex: '#3B82F6',
    description: 'Work in progress',
    icon: 'Play'
  },
  ON_HOLD: {
    label: 'On Hold',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    hex: '#EAB308',
    description: 'Work paused',
    requiresReason: true,
    icon: 'Pause'
  },
  COMPLETE: {
    label: 'Complete',
    color: 'bg-green-100 text-green-700 border-green-200',
    hex: '#22C55E',
    description: 'Physical work completed by technician',
    requiresAllLineItemsDone: true,
    icon: 'CheckCircle'
  },
  INCOMPLETE: {
    label: 'Incomplete',
    color: 'bg-amber-200 text-amber-900 border-amber-300',
    hex: '#D97706',
    description: 'Work has issues requiring operator review',
    operatorOnly: true,
    lockedForTechnician: true,
    icon: 'AlertTriangle'
  },
  QA_REVIEW: {
    label: 'QA Review',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    hex: '#8B5CF6',
    description: 'Send to Quality Assurance for review',
    operatorOnly: true,
    icon: 'ClipboardCheck'
  },
  RELEASE: {
    label: 'Release',
    color: 'bg-sky-100 text-sky-700 border-sky-200',
    hex: '#0EA5E9',
    description: 'Release car back to customer contact',
    operatorOnly: true,
    icon: 'Unlock'
  },
  BILLING: {
    label: 'Billing',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    hex: '#F97316',
    description: 'Billing in progress',
    operatorOnly: true,
    icon: 'DollarSign'
  },
  INVOICED: {
    label: 'Invoiced',
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    hex: '#06B6D4',
    description: 'Invoice sent to customer',
    operatorOnly: true,
    icon: 'Receipt'
  },
  CLOSED: {
    label: 'Closed',
    color: 'bg-gray-200 text-gray-800 border-gray-300',
    hex: '#4B5563',
    description: 'Final — job lifecycle complete',
    operatorOnly: true,
    isFinal: true,
    icon: 'Archive'
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'bg-red-100 text-red-700 border-red-200',
    hex: '#EF4444',
    description: 'Work cancelled',
    requiresConfirmation: true,
    requiresReason: true,
    minRole: 'OPERATOR',
    icon: 'XCircle'
  },
  RESCHEDULE: {
    label: 'Reschedule',
    color: 'bg-amber-100 text-amber-800 border-amber-300',
    hex: '#D97706',
    description: 'Needs rescheduling - select new date from scheduling form',
    requiresReason: true,
    isReschedule: true,
    icon: 'CalendarClock'
  },
  // 2026-07-18 — Historical handoff state applied when an SR is transferred
  // to a new responding facility. Not a live operational state. Rendered as
  // a locked timeline event in Work Order History.
  TRANSFERRED: {
    label: 'Transferred',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    hex: '#3B82F6',
    description: 'Work handed off due to facility transfer',
    icon: 'ArrowRightLeft'
  }
};

// ==========================================
// TRANSITION FUNCTIONS
// ==========================================

/**
 * Get valid transitions for a Service Request status
 * Does NOT include CANCELLED (handled separately) or SR_SCHEDULED (system-only)
 */
export const getValidSRTransitions = (currentStatus, userRole = 'OPERATOR') => {
  // SR_SCHEDULED and CANCELLED have no normal transitions
  if (currentStatus === 'SR_SCHEDULED' || currentStatus === 'CANCELLED') {
    return [];
  }
  return SR_STATUS_TRANSITIONS[currentStatus] || [];
};

/**
 * Get valid transitions for a Work Schedule status based on user role
 * 
 * TECHNICIAN transitions:
 * - SCHEDULED → IN_PROCESS
 * - IN_PROCESS → ON_HOLD, COMPLETE (if all line items done)
 * - ON_HOLD → IN_PROCESS
 * - COMPLETE, QA_REVIEW, RELEASE, BILLING, INVOICED, CLOSED, RESCHEDULE → (locked)
 * 
 * OPERATOR+ transitions:
 * - SCHEDULED → IN_PROCESS, ON_HOLD
 * - IN_PROCESS → ON_HOLD, COMPLETE (if all line items done)
 * - ON_HOLD → IN_PROCESS
 * - COMPLETE → QA_REVIEW (compliance review) OR RELEASE (direct release) - THE FORK
 * - QA_REVIEW → RELEASE, BILLING (bypass release)
 * - RELEASE → BILLING, QA_REVIEW (realized needs compliance)
 * - BILLING → INVOICED (main path), Special: Return to QA_REVIEW or RELEASE (corrections)
 * - INVOICED → CLOSED
 * - CLOSED → (FINAL - no changes)
 * - RESCHEDULE → SCHEDULED (after updating dates)
 * 
 * BLOCKED for ALL roles:
 * - IN_PROCESS → SCHEDULED (must use Reschedule button instead)
 */
export const getValidWSTransitions = (currentStatus, userRole = 'OPERATOR', lineItemsComplete = false) => {
  const isTechnician = userRole === 'TECHNICIAN';
  
  // CANCELLED has no transitions
  if (currentStatus === 'CANCELLED') {
    return [];
  }
  
  // CLOSED is FINAL - no changes allowed
  if (currentStatus === 'CLOSED') {
    return [];
  }
  
  // RESCHEDULE is locked for technicians
  if (currentStatus === 'RESCHEDULE') {
    if (isTechnician) {
      return []; // Locked - operator must handle via Reschedule button
    }
    // Operators use Reschedule button, not status change
    return [];
  }
  
  // INCOMPLETE: Locked for technicians, Operator+ can override to Complete, Reschedule, or Cancel
  if (currentStatus === 'INCOMPLETE') {
    if (isTechnician) {
      return []; // Locked - view only for technicians
    }
    // Operator+ can override to Complete (continue normal flow), or Reschedule
    // Cancel is handled as a special action, not a regular transition
    return ['COMPLETE', 'RESCHEDULE'];
  }
  
  // Technicians cannot transition to/from Operator-only statuses
  if (isTechnician) {
    // Technician cannot do anything from these statuses
    if (['COMPLETE', 'QA_REVIEW', 'RELEASE', 'BILLING', 'INVOICED', 'CLOSED'].includes(currentStatus)) {
      return [];
    }
    
    switch (currentStatus) {
      case 'SCHEDULED':
        return ['IN_PROCESS']; // Technicians can only start work
      case 'IN_PROCESS':
        const techOptions = ['ON_HOLD'];
        if (lineItemsComplete) {
          techOptions.push('COMPLETE');
        }
        return techOptions;
      case 'ON_HOLD':
        return ['IN_PROCESS']; // Resume work only
      default:
        return [];
    }
  }
  
  // Operator/Manager/Admin transitions
  // PROMPT 2: Updated transition rules
  switch (currentStatus) {
    case 'SCHEDULED':
      return ['IN_PROCESS', 'ON_HOLD'];
    case 'IN_PROCESS':
      // Block backward to SCHEDULED - must use Reschedule button
      const opOptions = ['ON_HOLD'];
      if (lineItemsComplete) {
        opOptions.push('COMPLETE');
      }
      return opOptions;
    case 'ON_HOLD':
      return ['IN_PROCESS']; // Resume work
    case 'COMPLETE':
      // Forward: QA Review or Release
      // Reschedule is handled in Corrections section (requires note)
      return ['QA_REVIEW', 'RELEASE'];
    case 'QA_REVIEW':
      // FIX 2: Fork between QA Review and Release - can move to either
      // Forward: Release (no note) or Billing
      // Reschedule handled in Corrections section (requires note)
      return ['RELEASE', 'BILLING'];
    case 'RELEASE':
      // FIX 2: Fork between QA Review and Release - can move to either
      // Forward: QA Review (no note) or Billing
      // Reschedule handled in Corrections section (requires note)
      return ['QA_REVIEW', 'BILLING'];
    case 'BILLING':
      // Forward: Invoiced
      // Corrections (QA_REVIEW, RELEASE) handled separately with note requirement
      return ['INVOICED'];
    case 'INVOICED':
      // Forward: Closed (final)
      // Corrections (BILLING, QA_REVIEW, RESCHEDULE) handled separately with note requirement
      return ['CLOSED'];
    default:
      return [];
  }
};

/**
 * Get special correction actions for Billing status (pull-back for errors)
 * Only available for Operator+ roles
 */
export const getBillingCorrections = (userRole = 'OPERATOR') => {
  const isTechnician = userRole === 'TECHNICIAN';
  if (isTechnician) return [];
  return ['QA_REVIEW', 'RELEASE']; // Can return to either for corrections
};

/**
 * PROMPT 2: Get correction/backward actions from Invoiced status
 * Only available for Operator+ roles
 * 
 * FIX 3: Linear path - only Return to Billing (from there user can go to QA Review/Release/Reschedule)
 */
export const getInvoicedCorrections = (userRole = 'OPERATOR') => {
  const isTechnician = userRole === 'TECHNICIAN';
  if (isTechnician) return [];
  return ['BILLING']; // Only Return to Billing - linear path
};

/**
 * PROMPT 2 FIX 3: Check if a status transition requires a note
 * Backward movements and cancellations always require notes
 */
export const transitionRequiresNote = (fromStatus, toStatus) => {
  // Cancel always requires note
  if (toStatus === 'CANCELLED') return true;
  
  // Reschedule from post-complete statuses requires note (not INVOICED - linear path required)
  if (toStatus === 'RESCHEDULE' && ['COMPLETE', 'QA_REVIEW', 'RELEASE'].includes(fromStatus)) {
    return true;
  }
  
  // All corrections/backward moves from Billing require notes
  if (fromStatus === 'BILLING' && ['QA_REVIEW', 'RELEASE'].includes(toStatus)) {
    return true;
  }
  
  // All corrections/backward moves from Invoiced require notes (only BILLING allowed now)
  if (fromStatus === 'INVOICED' && toStatus === 'BILLING') {
    return true;
  }
  
  return false;
};

// Legacy function for backward compatibility (uses old behavior)
export const getValidWSTransitionsLegacy = (currentStatus) => {
  // CANCELLED and RESCHEDULE have no normal linear transitions
  if (currentStatus === 'CANCELLED' || currentStatus === 'RESCHEDULE') {
    return [];
  }
  
  const currentIdx = WS_STATUS_ORDER.indexOf(currentStatus);
  if (currentIdx === -1) return [];
  
  const valid = [];
  // Block backward from IN_PROCESS to SCHEDULED
  if (currentIdx > 0 && !(currentStatus === 'IN_PROCESS' && WS_STATUS_ORDER[currentIdx - 1] === 'SCHEDULED')) {
    valid.push(WS_STATUS_ORDER[currentIdx - 1]);
  }
  if (currentIdx < WS_STATUS_ORDER.length - 1) valid.push(WS_STATUS_ORDER[currentIdx + 1]);
  return valid;
};

/**
 * Check if CANCELLED is available for an entity
 */
export const canSetCancelled = (currentStatus, entityType, userRole) => {
  if (!canCancel(userRole)) return false;
  
  // Cannot cancel if already cancelled
  if (currentStatus === 'CANCELLED') return false;
  
  // Cannot cancel SR_SCHEDULED (changes go through WS)
  if (entityType === 'sr' && currentStatus === 'SR_SCHEDULED') return false;
  
  return true;
};

/**
 * Check if UNCANCEL is available
 */
export const canSetUncancel = (currentStatus, userRole, previousStatus) => {
  if (!canUncancel(userRole)) return false;
  if (currentStatus !== 'CANCELLED') return false;
  // Must have a previous status to restore to
  return !!previousStatus;
};

/**
 * Check if RESCHEDULE status can be set on a Work Schedule via the Change Status dialog
 * 
 * PROMPT 2 FIX 3: Reschedule available from:
 * - SCHEDULED, IN_PROCESS, ON_HOLD, INCOMPLETE (early stages)
 * - COMPLETE, QA_REVIEW, RELEASE (before billing - requires note)
 * 
 * NOT available from: BILLING, INVOICED (must use linear path: Invoiced → Billing → QA/Release → Reschedule), CLOSED, CANCELLED, already RESCHEDULE
 */
export const canSetReschedule = (currentStatus) => {
  const rescheduleAllowed = [
    'SCHEDULED', 'IN_PROCESS', 'ON_HOLD', 'INCOMPLETE',  // Early stages
    'COMPLETE', 'QA_REVIEW', 'RELEASE'                    // Before billing (not INVOICED - linear path required)
  ];
  return rescheduleAllowed.includes(currentStatus);
};

/**
 * RULE 3: Check if the dedicated Reschedule button should be visible (Operator+ only)
 * Only visible when status = RESCHEDULE (so operator can act on tech's request)
 */
export const canUseRescheduleButton = (currentStatus, userRole) => {
  // Only Operator+ can use the Reschedule button
  if (userRole === 'TECHNICIAN') return false;
  // Only show when status is already RESCHEDULE
  return currentStatus === 'RESCHEDULE';
};

/**
 * Check if CANCEL is available based on role
 * Technicians cannot cancel - only Operator+ can
 */
export const canSetCancelledForRole = (currentStatus, entityType, userRole) => {
  // Technicians cannot cancel
  if (userRole === 'TECHNICIAN') return false;
  return canSetCancelled(currentStatus, entityType, userRole);
};

/**
 * Get the locked status message for technicians
 */
export const getTechnicianLockedMessage = (currentStatus) => {
  if (currentStatus === 'COMPLETE') {
    return 'This job is complete. An operator will handle the next steps.';
  }
  if (currentStatus === 'RESCHEDULE') {
    return 'This job is pending rescheduling by an operator.';
  }
  if (currentStatus === 'CANCELLED') {
    return 'This job has been cancelled.';
  }
  if (currentStatus === 'CLOSED') {
    return 'This job is closed and archived.';
  }
  // All other Operator-only statuses
  if (['QA_REVIEW', 'RELEASE', 'BILLING', 'INVOICED'].includes(currentStatus)) {
    return 'This job is being processed by the office. Status changes are handled by operators.';
  }
  return null;
};

/**
 * Check if SR status transition is valid
 */
export const canTransitionSR = (fromStatus, toStatus) => {
  // Cannot manually transition to SR_SCHEDULED
  if (toStatus === 'SR_SCHEDULED') return false;
  return getValidSRTransitions(fromStatus).includes(toStatus);
};

/**
 * Check if WS status transition is valid (linear chain only)
 */
export const canTransitionWS = (fromStatus, toStatus) => {
  return getValidWSTransitions(fromStatus).includes(toStatus);
};

/**
 * Get status configuration (works for both SR and WS)
 */
export const getStatusConfig = (status, type = 'auto') => {
  if (type === 'sr' || SR_STATUS_CONFIG[status]) {
    return SR_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  }
  return WS_STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
};

/**
 * Calculate days in status
 */
export const calculateDaysInStatus = (statusEnteredAt) => {
  if (!statusEnteredAt) return 0;
  try {
    const entered = new Date(statusEnteredAt);
    const now = new Date();
    return Math.floor((now - entered) / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
};

/**
 * Check if status needs follow-up
 */
export const needsFollowup = (status, daysInStatus) => {
  const threshold = SR_FOLLOWUP_THRESHOLDS[status];
  return threshold && daysInStatus >= threshold;
};

/**
 * Check if a record should be auto-archived (60 days in CANCELLED)
 */
export const shouldAutoArchive = (status, cancelledAt) => {
  if (status !== 'CANCELLED' || !cancelledAt) return false;
  const days = calculateDaysInStatus(cancelledAt);
  return days >= CANCELLED_AUTO_ARCHIVE_DAYS;
};
